import { useEffect, useRef, useState } from "react";
import { customerOwnerOptions, hasActiveCustomerOwner } from "../lib/customerAccess.js";
import { CustomerLink, EmployeeLink } from "../components/recordLinks.jsx";
import { CheckboxField, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function EngagementView({ resources, customers, users, onCreate, openCustomer, openEmployee, workflowDraft, onWorkflowDraftHandled }) {
  const hasCustomers = customers.length > 0;
  const customerOptions = hasCustomers ? customerOwnerOptions(customers, users) : [{ value: "", label: "No customers available" }];
  const [ticketForm, setTicketForm] = useState({ customerId: customers[0]?.id ?? "", queryType: "Product", priority: "Medium", status: "Open", summary: "" });
  const [messageForm, setMessageForm] = useState({ customerId: customers[0]?.id ?? "", channel: "WhatsApp", template: "Follow-up", status: "Sent", message: "", consentRecorded: false });
  const [callForm, setCallForm] = useState({ customerId: customers[0]?.id ?? "", direction: "Incoming", summary: "", outcome: "Follow-up required" });
  const ticketFormRef = useRef(null);
  const messageFormRef = useRef(null);
  const callFormRef = useRef(null);
  const canCreateTicket = hasCustomers && hasActiveCustomerOwner(ticketForm.customerId, customers, users);
  const messageCustomerHasActiveOwner = hasCustomers && hasActiveCustomerOwner(messageForm.customerId, customers, users);
  const messageCustomer = customers.find((customer) => customer.id === messageForm.customerId);
  const messageNeedsWhatsAppConsent =
    messageForm.channel === "WhatsApp" &&
    messageCustomer?.consentOptIn === false &&
    messageForm.consentRecorded !== true;
  const canSendMessage = messageCustomerHasActiveOwner && !messageNeedsWhatsAppConsent;
  const canLogCall = hasCustomers && hasActiveCustomerOwner(callForm.customerId, customers, users);
  const customerIds = customers.map((customer) => customer.id).join("|");
  const whatsappProvider = resources.communications.find((item) => item.channel === "WhatsApp")?.provider ?? "mock-whatsapp-rest";
  const callProvider = resources.callLogs[0]?.provider ?? "manual-call-center";

  useEffect(() => {
    const fallbackCustomerId = customers[0]?.id || "";
    const resolveCustomerId = (currentId) => (customers.some((customer) => customer.id === currentId) ? currentId : fallbackCustomerId);
    setTicketForm((current) => ({ ...current, customerId: resolveCustomerId(current.customerId) }));
    setMessageForm((current) => {
      const customerId = resolveCustomerId(current.customerId);
      return {
        ...current,
        customerId,
        consentRecorded: customerId === current.customerId ? current.consentRecorded : false,
      };
    });
    setCallForm((current) => ({ ...current, customerId: resolveCustomerId(current.customerId) }));
  }, [customerIds]);

  useEffect(() => {
    if (!workflowDraft || !["message", "ticket", "call"].includes(workflowDraft.type)) return;
    const customer = customers.find((item) => item.id === workflowDraft.customerId);
    if (!customer) {
      onWorkflowDraftHandled?.(workflowDraft.id);
      return;
    }
    if (workflowDraft.type === "message") {
      setMessageForm({
        customerId: customer.id,
        channel: workflowDraft.channel || "WhatsApp",
        template: workflowDraft.template || "Follow-up",
        status: workflowDraft.status || "Sent",
        message: workflowDraft.message || "",
        consentRecorded: workflowDraft.consentRecorded ?? (customer.consentOptIn !== false),
      });
      focusForm(messageFormRef);
    }
    if (workflowDraft.type === "ticket") {
      setTicketForm({
        customerId: customer.id,
        queryType: workflowDraft.queryType || "Product",
        priority: workflowDraft.priority || "Medium",
        status: workflowDraft.status || "Open",
        summary: workflowDraft.summary || "",
      });
      focusForm(ticketFormRef);
    }
    if (workflowDraft.type === "call") {
      setCallForm({
        customerId: customer.id,
        direction: workflowDraft.direction || "Outgoing",
        summary: workflowDraft.summary || "",
        outcome: workflowDraft.outcome || "Follow-up required",
      });
      focusForm(callFormRef);
    }
    onWorkflowDraftHandled?.(workflowDraft.id);
  }, [workflowDraft?.id, customerIds]);

  return (
    <>
      <Topbar title="Support & Messaging" subtitle="Phase 5: call center tickets, SLA visibility, WhatsApp-style templates, and communication history." />
      <section className="integration-strip">
        <div>
          <span>WhatsApp Adapter</span>
          <strong>{whatsappProvider}</strong>
        </div>
        <div>
          <span>Call Adapter</span>
          <strong>{callProvider}</strong>
        </div>
        <div>
          <span>Consent Opt-ins</span>
          <strong>{customers.filter((customer) => customer.consentOptIn !== false).length}</strong>
        </div>
      </section>
      <section className="content-grid">
        <div ref={ticketFormRef}>
          <Panel title="Create Ticket">
            {!hasCustomers ? <div className="notice">No customers are available in this reporting scope. Add or assign a prospect before creating support records.</div> : null}
            {hasCustomers && !canCreateTicket ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before creating support records.</div> : null}
            <SimpleForm disabled={!canCreateTicket} onSubmit={() => onCreate("/tickets", ticketForm)}>
	              <SelectField
	                label="Customer"
	                value={ticketForm.customerId}
	                options={customerOptions}
	                disabled={!hasCustomers}
	                actionLabel="Add prospect"
	                onAction={openCustomer ? () => openCustomer(null) : undefined}
	                secondaryActionLabel="Open selected prospect"
	                secondaryActionDisabled={!ticketForm.customerId}
	                onSecondaryAction={openCustomer ? () => openCustomer(ticketForm.customerId) : undefined}
	                onChange={(customerId) => setTicketForm({ ...ticketForm, customerId })}
	              />
              <SelectField label="Query type" value={ticketForm.queryType} options={["Product", "Pricing", "Complaint", "Order"]} onChange={(queryType) => setTicketForm({ ...ticketForm, queryType })} />
              <SelectField label="Priority" value={ticketForm.priority} options={["Low", "Medium", "High"]} onChange={(priority) => setTicketForm({ ...ticketForm, priority })} />
              <TextField label="Summary" value={ticketForm.summary} onChange={(summary) => setTicketForm({ ...ticketForm, summary })} />
            </SimpleForm>
          </Panel>
        </div>
        <div ref={messageFormRef}>
          <Panel title="Send Message">
            {!hasCustomers ? <div className="notice">No customers are available in this reporting scope. Add or assign a prospect before sending messages.</div> : null}
            {hasCustomers && !messageCustomerHasActiveOwner ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before sending messages.</div> : null}
            {messageNeedsWhatsAppConsent ? <div className="notice">Selected customer has no WhatsApp opt-in on file. Record fresh consent before sending WhatsApp.</div> : null}
            <SimpleForm disabled={!canSendMessage} onSubmit={() => onCreate("/communications", messageForm)}>
	              <SelectField
	                label="Customer"
	                value={messageForm.customerId}
	                options={customerOptions}
	                disabled={!hasCustomers}
	                actionLabel="Add prospect"
	                onAction={openCustomer ? () => openCustomer(null) : undefined}
	                secondaryActionLabel="Open selected prospect"
	                secondaryActionDisabled={!messageForm.customerId}
	                onSecondaryAction={openCustomer ? () => openCustomer(messageForm.customerId) : undefined}
	                onChange={(customerId) => setMessageForm({ ...messageForm, customerId, consentRecorded: false })}
	              />
              <SelectField label="Channel" value={messageForm.channel} options={["WhatsApp", "Call", "Note"]} onChange={(channel) => setMessageForm({ ...messageForm, channel })} />
              <SelectField label="Template" value={messageForm.template} options={["Intro", "Product recommendation", "Follow-up", "Order update", "Retention"]} onChange={(template) => setMessageForm({ ...messageForm, template })} />
              <TextField label="Message" value={messageForm.message} onChange={(message) => setMessageForm({ ...messageForm, message })} />
              <CheckboxField label="Consent recorded" checked={messageForm.consentRecorded !== false} onChange={(consentRecorded) => setMessageForm({ ...messageForm, consentRecorded })} />
            </SimpleForm>
          </Panel>
        </div>
        <div ref={callFormRef}>
          <Panel title="Log Call">
            {!hasCustomers ? <div className="notice">No customers are available in this reporting scope. Add or assign a prospect before logging calls.</div> : null}
            {hasCustomers && !canLogCall ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before logging calls.</div> : null}
            <SimpleForm disabled={!canLogCall} onSubmit={() => onCreate("/call-logs", callForm)}>
	              <SelectField
	                label="Customer"
	                value={callForm.customerId}
	                options={customerOptions}
	                disabled={!hasCustomers}
	                actionLabel="Add prospect"
	                onAction={openCustomer ? () => openCustomer(null) : undefined}
	                secondaryActionLabel="Open selected prospect"
	                secondaryActionDisabled={!callForm.customerId}
	                onSecondaryAction={openCustomer ? () => openCustomer(callForm.customerId) : undefined}
	                onChange={(customerId) => setCallForm({ ...callForm, customerId })}
	              />
              <SelectField label="Direction" value={callForm.direction} options={["Incoming", "Outgoing"]} onChange={(direction) => setCallForm({ ...callForm, direction })} />
              <TextField label="Summary" value={callForm.summary} onChange={(summary) => setCallForm({ ...callForm, summary })} />
              <SelectField label="Outcome" value={callForm.outcome} options={["Connected", "Not connected", "Follow-up required", "Escalated", "Resolved"]} onChange={(outcome) => setCallForm({ ...callForm, outcome })} />
            </SimpleForm>
          </Panel>
        </div>
      </section>
      <section className="content-grid spaced">
        <Panel title="Tickets">
	          <ResourceTable
              rows={resources.tickets}
              columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["queryType", "Type"], ["priority", "Priority"], ["status", "Status"], ["slaDueDate", "SLA"]]}
              action={(row) => (
                <div className="row-actions">
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startTicketFrom(row)}>
                    New Ticket
                  </button>
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startMessageFromTicket(row)}>
                    Message
                  </button>
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startCallFromTicket(row)}>
                    Call
                  </button>
                </div>
              )}
            />
        </Panel>
        <Panel title="Communication History">
	          <ResourceTable
              rows={resources.communications}
              columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["channel", "Channel"], ["template", "Template"], ["status", "Status"], ["provider", "Provider"], ["consentRecorded", "Consent"]]}
              action={(row) => (
                <div className="row-actions">
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startMessageFrom(row)}>
                    Message Again
                  </button>
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startCallFromMessage(row)}>
                    Call
                  </button>
                </div>
              )}
            />
        </Panel>
        <Panel title="Call Logs">
	          <ResourceTable
              rows={resources.callLogs}
              columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["direction", "Direction"], ["customerNumber", "Number"], ["outcome", "Outcome"], ["userId", "Sales Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />], ["summary", "Summary"]]}
              action={(row) => (
                <div className="row-actions">
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startCallFrom(row)}>
                    Log Follow-up
                  </button>
                  <button className="mini-btn" type="button" disabled={!hasCustomer(row.customerId)} onClick={() => startMessageFromCall(row)}>
                    Message
                  </button>
                </div>
              )}
            />
        </Panel>
      </section>
      <section className="content-grid spaced">
        <Panel title="Message Templates">
          <ResourceTable
            rows={resources.messageTemplates}
            columns={[["name", "Template"], ["lifecycleStage", "Lifecycle"], ["channel", "Channel"], ["body", "Body"]]}
            action={(row) => (
              <button className="mini-btn" type="button" disabled={!hasCustomers} onClick={() => startMessageFromTemplate(row)}>
                Use Template
              </button>
            )}
          />
        </Panel>
        <Panel title="Knowledge Support" subtitle="FAQ basics for pricing, payments, demos, policies, customization, and integrations.">
          <FaqGuide faqs={resources.faqs} />
        </Panel>
      </section>
    </>
  );

  function hasCustomer(customerId) {
    return customers.some((customer) => customer.id === customerId);
  }

  function startTicketFrom(row) {
    if (!hasCustomer(row.customerId)) return;
    setTicketForm({
      customerId: row.customerId,
      queryType: row.queryType || "Product",
      priority: row.priority || "Medium",
      status: "Open",
      summary: "",
    });
    focusForm(ticketFormRef);
  }

  function startMessageFrom(row) {
    if (!hasCustomer(row.customerId)) return;
    setMessageForm({
      customerId: row.customerId,
      channel: row.channel || "WhatsApp",
      template: row.template || "Follow-up",
      status: "Sent",
      message: "",
      consentRecorded: row.consentRecorded !== false,
    });
    focusForm(messageFormRef);
  }

  function startMessageFromTicket(row) {
    if (!hasCustomer(row.customerId)) return;
    const customer = customers.find((item) => item.id === row.customerId);
    setMessageForm({
      customerId: row.customerId,
      channel: "WhatsApp",
      template: row.status === "Resolved" ? "Order update" : "Follow-up",
      status: "Sent",
      message: supportFollowUpMessage(row),
      consentRecorded: customer?.consentOptIn !== false,
    });
    focusForm(messageFormRef);
  }

  function startMessageFromCall(row) {
    if (!hasCustomer(row.customerId)) return;
    const customer = customers.find((item) => item.id === row.customerId);
    setMessageForm({
      customerId: row.customerId,
      channel: "WhatsApp",
      template: row.outcome === "Resolved" ? "Order update" : "Follow-up",
      status: "Sent",
      message: row.summary ? `Following up on our call: ${row.summary}` : "Following up on our call.",
      consentRecorded: customer?.consentOptIn !== false,
    });
    focusForm(messageFormRef);
  }

  function startCallFrom(row) {
    if (!hasCustomer(row.customerId)) return;
    setCallForm({
      customerId: row.customerId,
      direction: "Outgoing",
      summary: "",
      outcome: row.outcome === "Resolved" ? "Follow-up required" : row.outcome || "Follow-up required",
    });
    focusForm(callFormRef);
  }

  function startCallFromTicket(row) {
    if (!hasCustomer(row.customerId)) return;
    setCallForm({
      customerId: row.customerId,
      direction: "Outgoing",
      summary: supportFollowUpMessage(row),
      outcome: row.status === "Resolved" ? "Resolved" : "Follow-up required",
    });
    focusForm(callFormRef);
  }

  function startCallFromMessage(row) {
    if (!hasCustomer(row.customerId)) return;
    setCallForm({
      customerId: row.customerId,
      direction: "Outgoing",
      summary: row.message ? `Follow up on message: ${row.message}` : "Follow up on last message.",
      outcome: row.status === "Delivered" || row.status === "Sent" ? "Follow-up required" : row.status || "Follow-up required",
    });
    focusForm(callFormRef);
  }

  function startMessageFromTemplate(row) {
    if (!hasCustomers) return;
    setMessageForm((current) => ({
      ...current,
      channel: row.channel || current.channel,
      template: row.name || current.template,
      message: row.body || current.message,
    }));
    focusForm(messageFormRef);
  }

  function focusForm(ref) {
    window.setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      ref.current?.querySelector("select, input, textarea")?.focus({ preventScroll: true });
    }, 0);
  }
}

function supportFollowUpMessage(row) {
  const type = row.queryType || "support";
  return row.summary ? `Following up on ${type} ticket: ${row.summary}` : `Following up on ${type} ticket.`;
}

function FaqGuide({ faqs = [] }) {
  if (!faqs.length) return <div className="empty">No FAQ records yet.</div>;
  const groups = faqs.reduce((accumulator, faq) => {
    const category = faq.category || "General";
    if (!accumulator.has(category)) accumulator.set(category, []);
    accumulator.get(category).push(faq);
    return accumulator;
  }, new Map());

  return (
    <div className="faq-guide">
      {[...groups.entries()].map(([category, items]) => (
        <section className="faq-category" key={category}>
          <h3>{category}</h3>
          <div className="faq-list">
            {items.map((faq) => (
              <article className="faq-item" key={faq.id}>
                <h4>{faq.question}</h4>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
